<script lang="ts" setup>
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";

import { isOn } from "@/libs/features.ts";
import { format, reservedThrough, sum, totalsOn } from "@/libs/amount.ts";

import { toggle } from "@/repositories/transactions.ts";

import { useAccount } from "@/stores/account.ts";
import { useReservations } from "@/stores/reservations.ts";
import { useTransactions } from "@/stores/transactions.ts";
import { useDateRange } from "@/components/DateRange/state.ts";
import { displayRows, onEdit } from "@/functions/transactions.ts";
import ItemTransactions from "@/components/ItemTransactions.vue";

import type { ByName } from "@/types/transaction";

const account = useAccount();
const transactions = useTransactions();
const reservations = useReservations();
const dateRange = useDateRange();

const { initialBalance } = storeToRefs(account);
const { byNameByMonth, byMonth } = storeToRefs(transactions);
const { incomeTax } = storeToRefs(reservations);
const { months } = storeToRefs(dateRange);

// Cumulative reserved fund per month: opening history + every delta up to that month.
const cumulativeReservations = computed(() =>
  months.value.map((month) =>
    reservedThrough(
      incomeTax.value?.opening ?? 0,
      incomeTax.value?.by_day,
      month.format("YYYY-MM"),
    ),
  ),
);

// Gross cash with mutations applied; free cash subtracts the reserved fund.
const balances = computed(() =>
  months.value.map((month, index) => {
    const gross = totalsOn(byMonth.value, month.format("YYYY-MM"), initialBalance.value).balance;

    return isOn("income-tax") ? gross - (cumulativeReservations.value[index] ?? 0) : gross;
  }),
);

const onToggle = async (id: number) => {
  const isDisabled = await toggle(id);

  transactions.toggle(id, isDisabled);
};

const expanded = ref<string[]>([]);

const transactionsOf = (items: ByName[string]) =>
  months.value.flatMap(
    (month) => items.transactionsPerMonth[month.format("YYYY-MM")]?.transactions ?? [],
  );

// v-for object keys type as string | number; normalize once.
const isExpanded = (name: string | number) => expanded.value.includes(String(name));

const toggleExpand = (name: string | number) => {
  const key = String(name);

  expanded.value = expanded.value.includes(key)
    ? expanded.value.filter((n) => n !== key)
    : [...expanded.value, key];
};

// Rows with a single display line (one-off, or one recurring series) open edit directly.
const onRowClick = (name: string | number, items: ByName[string]) => {
  if (displayRows(transactionsOf(items)).length > 1) {
    toggleExpand(name);

    return;
  }

  onEdit(items.id, items.type);
};
</script>

<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr class="divide-x divide-gray-200">
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Naam
                </th>
                <th
                  v-for="month in months"
                  :key="month.format('YYYY-MM')"
                  class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase"
                  scope="col"
                >
                  {{ month.format("MMMM YYYY") }}
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-gray-200 bg-white">
              <template v-for="(itemsPerMonth, name) in byNameByMonth" :key="name">
                <tr
                  class="divide-x divide-gray-200 cursor-pointer"
                  @click="onRowClick(name, itemsPerMonth)"
                >
                  <th
                    class="py-4 px-4 flex flex-row gap-x-3 items-center text-sm text-left font-semibold whitespace-nowrap text-gray-900"
                  >
                    <input
                      type="checkbox"
                      :checked="itemsPerMonth.isEnabled"
                      @click.stop
                      @change="onToggle(itemsPerMonth.id)"
                    />
                    {{ name }}
                    <template v-if="displayRows(transactionsOf(itemsPerMonth)).length > 1">
                      <ChevronDownIcon v-if="isExpanded(name)" class="size-4 ml-auto shrink-0" />
                      <ChevronRightIcon v-else class="size-4 ml-auto shrink-0" />
                    </template>
                  </th>
                  <td
                    v-for="month in months"
                    :key="month.format('YYYY-MM')"
                    :class="[
                      itemsPerMonth.transactionsPerMonth[month.format('YYYY-MM')]?.transactions[0]
                        .type === 'expense'
                        ? 'text-red-600'
                        : 'text-gray-900',
                      'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                    ]"
                  >
                    {{
                      format(
                        sum(
                          itemsPerMonth.transactionsPerMonth[month.format("YYYY-MM")]?.transactions,
                        ),
                      )
                    }}
                  </td>
                </tr>

                <ItemTransactions
                  v-if="isExpanded(name)"
                  :transactions="transactionsOf(itemsPerMonth)"
                  :periods="months"
                  period-format="YYYY-MM"
                />
              </template>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Totaal in
                </th>
                <td
                  v-for="month in months"
                  :key="month.format('YYYY-MM')"
                  class="py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0"
                >
                  {{ format(byMonth[month.format("YYYY-MM")]?.totals.in) }}
                </td>
              </tr>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Totaal uit
                </th>
                <td
                  v-for="month in months"
                  :key="month.format('YYYY-MM')"
                  class="py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0"
                >
                  {{ format(byMonth[month.format("YYYY-MM")]?.totals.out) }}
                </td>
              </tr>

              <tr v-if="isOn('income-tax')">
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Reserveringen
                </th>
                <td
                  v-for="(cumulativeReservation, index) in cumulativeReservations"
                  :key="months[index].format('YYYY-MM')"
                  :class="[
                    cumulativeReservation < 0 ? 'text-red-600' : 'text-gray-900',
                    'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                  ]"
                >
                  {{ format(cumulativeReservation) }}
                </td>
              </tr>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  {{ isOn("income-tax") ? "Besteedbaar saldo" : "Saldo" }}
                </th>
                <td
                  v-for="(balance, index) in balances"
                  :key="months[index].format('YYYY-MM')"
                  :class="[
                    balance < 0 ? 'text-red-600' : 'text-gray-900',
                    'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                  ]"
                >
                  {{ format(balance) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
